import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/lib/api';

/** GET /share/links/:token and POST /share/code (RL-986 ShareSummaryDto). */
export interface SharedRecord {
  sharedVia: 'link' | 'code';
  expiresAt: string;
  generatedAt: string;
  patient: {
    name: string;
    dateOfBirth: string | null;
    sex: 'male' | 'female' | null;
    identityType: 'mykad' | 'passport' | null;
    identityMasked: string | null;
  };
  allergies: {
    substance: string;
    reaction: string | null;
    severity: 'mild' | 'moderate' | 'severe' | null;
    status: 'confirmed' | 'reported';
    recordedAt: string | null;
  }[];
  medicines: {
    medicine: string;
    dose: string | null;
    frequency: string | null;
    durationDays: number | null;
    prescribedOn: string | null;
  }[];
  results: {
    testName: string;
    value: string | null;
    unit: string | null;
    referenceRange: string | null;
    flag: string | null;
    resultDate: string | null;
  }[];
  problems: string[] | null;
  periodMonths: number;
  unavailable: string[];
  notice: string;
}

/**
 * RL-1911: open a share link. Every call is one opening in the patient's
 * access log, so it is fetched once and never refetched in the background.
 * Sent without the staff session (a signed-in browser stays anonymous), and
 * dropped from the cache when the page closes.
 */
export function useSharedLink(token: string) {
  const qc = useQueryClient();
  useEffect(() => () => qc.removeQueries({ queryKey: ['share'] }), [qc]);
  return useQuery({
    queryKey: ['share', 'link', token],
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: ({ signal }) =>
      api
        .get<SharedRecord>(`/share/links/${encodeURIComponent(token)}`, {
          signal,
          anonymous: true,
        })
        .then((r) => r.data),
  });
}

/** RL-1911: a one-time code with the patient's date of birth (used up once opened). */
export function useOpenShareCode() {
  return useMutation({
    mutationFn: (body: { code: string; dateOfBirth: string }) =>
      api
        .post<SharedRecord>('/share/code', body, { anonymous: true })
        .then((r) => r.data),
  });
}
